'use es6';

import PropTypes from 'prop-types';
import { memo, useState, useEffect } from 'react';
import compose from 'transmute/compose';
import { useSelector } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { List, Range } from 'immutable';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILoadingDots from 'UIComponents/loading/UILoadingDots';
import UIOverlay from 'UIComponents/overlay/UIOverlay';
import { getLinkUse } from 'MeetingsBase/utils/color';
import DatePickerButton from 'MeetingsLib/components/schedule/date/DatePickerButton';
import ZeroState from 'MeetingsLib/components/schedule/ZeroState';
import { getEditLinkURL as getEditLinkURLSelector, getUserTimezone, getEvent as getEventSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getAvailableDates as getAvailableDatesSelector } from 'MeetingsLib/selectors/AvailabilitySelectors';
import { getIsFetchingPublicAvailability } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { getCurrentTime } from 'MeetingsLib/selectors/RESTParamSelectors';
import { getConfiguredAvailabilityByDate, getTroubleshootTimesForSelectedDurationByDay, getConfiguredBookableDates, getMinimumNoticeTimeBusyDays, isTroubleshootingSelector } from 'MeetingsLib/selectors/TroubleshootingSelectors';
import Branding from 'MeetingsBase/models/Branding';
import UserProfile from 'MeetingsBase/models/UserProfile';
import EnsureAvailableDate from 'MeetingsLib/components/schedule/date/EnsureAvailableDate';
import styled from 'styled-components';
import { HiddenAriaStatus } from 'MeetingsLib/components/decorative/HiddenAriaStatus';
import DatePickerHeader from './DatePickerHeader';
import { useIsLoadingLinkAvailabilityPage, useSetMonthOffset } from 'MeetingsLib/context/PublicAvailabilityInfoContext';
import { useIsLoadingTroubleshootPage, useSetTroubleshootMonthOffset } from 'MeetingsLib/context/TroubleshootTimesContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTable = styled.table.withConfig({
  displayName: "DatePicker__StyledTable",
  componentId: "sc-13kz6l6-0"
})(["table-layout:fixed;width:100%;margin:0;"]);
const StyledCaption = styled.caption.withConfig({
  displayName: "DatePicker__StyledCaption",
  componentId: "sc-13kz6l6-1"
})(["font-size:0;line-height:0;"]);
const StyledOverlay = styled(UIOverlay).withConfig({
  displayName: "DatePicker__StyledOverlay",
  componentId: "sc-13kz6l6-2"
})(["background-color:rgba(45,62,80,0) !important;"]);
const StyledSkipNav = styled.a.withConfig({
  displayName: "DatePicker__StyledSkipNav",
  componentId: "sc-13kz6l6-3"
})(["position:absolute;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden;"]);
const NUM_DAYS = 7;
const NUM_WEEKS = 5;
const DAY_RANGE = Range().take(NUM_DAYS).toArray();
const propTypes = {
  // passed
  branding: PropTypes.instanceOf(Branding).isRequired,
  compact: PropTypes.bool,
  profile: PropTypes.instanceOf(UserProfile),
  setEventDate: PropTypes.func.isRequired,
  DatePickerMarker: PropTypes.func.isRequired,
  TimePickerMarker: PropTypes.func.isRequired
};
function DatePicker({
  branding,
  compact,
  profile,
  setEventDate,
  DatePickerMarker,
  TimePickerMarker
}) {
  const bookableDates = useSelector(state => getConfiguredBookableDates(state));
  const configuredAvailabilityByDate = useSelector(state => getConfiguredAvailabilityByDate(state));
  const currentTime = useSelector(state => getCurrentTime(state));
  const availableDateOptions = useSelector(state => getAvailableDatesSelector(state));
  const editLinkURL = useSelector(state => getEditLinkURLSelector(state));
  const troubleshootTimesForSelectedDurationByDay = useSelector(state => getTroubleshootTimesForSelectedDurationByDay(state));
  const isFetchingPublicAvailability = useSelector(state => getIsFetchingPublicAvailability(state));
  const minimumNoticeTimeBusyDays = useSelector(state => getMinimumNoticeTimeBusyDays(state));
  const isTroubleshooting = useSelector(state => isTroubleshootingSelector(state));
  const userTimezone = useSelector(state => getUserTimezone(state));
  const selectedDate = useSelector(state => getEventSelector(state).dateString);
  const setMonthOffset = useSetMonthOffset();
  const setTroubleshootMonthOffset = useSetTroubleshootMonthOffset();
  const isLoadingLinkAvailabilityPage = useIsLoadingLinkAvailabilityPage();
  const isLoadingTroubleshootPage = useIsLoadingTroubleshootPage();
  const isLoadingPage = isLoadingLinkAvailabilityPage || isLoadingTroubleshootPage;
  const [monthsAhead, setMonthsAhead] = useState(0);
  useEffect(() => setMonthsAhead(0), [isTroubleshooting, setMonthsAhead]);
  const minAvailableDay = I18n.moment(availableDateOptions.min());
  const maxAvailableDay = I18n.moment(availableDateOptions.max());
  const baseMonth = isTroubleshooting ? I18n.moment() : I18n.moment(availableDateOptions.min());
  const startOfMonth = I18n.moment(baseMonth).startOf('month').add(monthsAhead, 'months');
  const endOfMonth = I18n.moment(baseMonth).add(monthsAhead, 'months').endOf('month');
  const localeData = I18n.moment.localeData(I18n.locale) || I18n.moment.localeData();
  // some locales start their week on Monday
  const daysToShiftForward = localeData._week.dow;
  function getDaysToShiftBack(dateObject) {
    const daysFromSunday = dateObject.isoWeekday() % 7;

    /*
     * if the month starts on Sunday and we are in a locale
     * that starts weeks on Monday, we would cut off the first
     * Sunday unless we rendered the entire previous week
     */
    if (daysFromSunday === 0 && daysToShiftForward === 1) {
      return 6;
    }
    return daysFromSunday - daysToShiftForward;
  }
  function getStartOfWeek(dateObject) {
    const daysToShiftBack = getDaysToShiftBack(dateObject);
    return I18n.moment(dateObject).subtract(daysToShiftBack, 'days');
  }
  const startOfWeek = getStartOfWeek(startOfMonth);
  const lastDisplayedDay = I18n.moment(startOfWeek).add({
    days: NUM_DAYS - 1,
    weeks: NUM_WEEKS - 1
  });
  const availabilityOutsideOfDisplayed = lastDisplayedDay.isBefore(minAvailableDay) || lastDisplayedDay.isBefore(maxAvailableDay);
  const minOrMaxAvailabilityNotDisplayed = lastDisplayedDay.isBefore(endOfMonth) && availabilityOutsideOfDisplayed;
  useEffect(() => {
    if (monthsAhead) {
      setMonthOffset(monthsAhead);
    }
  }, [monthsAhead, setMonthOffset]);
  useEffect(() => {
    if (isTroubleshooting) {
      setTroubleshootMonthOffset(monthsAhead);
    }
  }, [isTroubleshooting, setTroubleshootMonthOffset, monthsAhead]);
  function handleSetEventDate(date) {
    if (isTroubleshooting || availableDateOptions.includes(date)) {
      setEventDate(date);
    }
  }
  const abbreviatedWeekdays = new List(localeData._weekdaysShort);
  const daysToShift = abbreviatedWeekdays.take(daysToShiftForward);
  const weekdayNames = abbreviatedWeekdays.splice(0, daysToShiftForward).concat(daysToShift);
  function getWeekRange(extraWeek) {
    const weeksToRender = extraWeek ? NUM_WEEKS + 1 : NUM_WEEKS;
    return Range().take(weeksToRender).toArray();
  }
  const renderDays = weekNumber => {
    return DAY_RANGE.map(daysToAdd => {
      const date = I18n.moment(startOfWeek).add({
        days: daysToAdd,
        weeks: weekNumber
      }).format('YYYY-MM-DD');
      return /*#__PURE__*/_jsx(DatePickerButton, {
        daysToAdd: daysToAdd,
        branding: branding,
        currentTime: currentTime,
        date: date,
        availableDates: availableDateOptions,
        handleSetEventDate: handleSetEventDate
        // troubleshoot mode
        ,
        bookableDates: bookableDates,
        configuredAvailabilityByDate: configuredAvailabilityByDate,
        editLinkURL: editLinkURL,
        troubleshootTimesForSelectedDurationByDay: troubleshootTimesForSelectedDurationByDay,
        minimumNoticeTimeBusyDays: minimumNoticeTimeBusyDays,
        isTroubleshooting: isTroubleshooting,
        userTimezone: userTimezone,
        isLoadingLinkAvailabilityPage: isLoadingPage
      }, `${date}-${daysToAdd}`);
    });
  };
  function renderWeeksOrEmpty() {
    if (compact && !availableDateOptions.count() && !isFetchingPublicAvailability) {
      // Render empty state for compact view
      return /*#__PURE__*/_jsx("div", {
        className: "align-center m-top-12",
        children: /*#__PURE__*/_jsx(ZeroState, {
          profile: profile,
          linkUse: getLinkUse(branding.primaryColor),
          TimePickerMarker: TimePickerMarker
        })
      });
    }
    const currentMonth = startOfMonth.format('MMMM');
    const weekDaysClasses = "title-case " + (compact ? 'm-bottom-2' : 'm-bottom-4');
    const weeks = getWeekRange(minOrMaxAvailabilityNotDisplayed).map(weeksToAdd => {
      return /*#__PURE__*/_jsx("tr", {
        className: "extraWeek",
        children: renderDays(weeksToAdd)
      }, weeksToAdd);
    });
    return /*#__PURE__*/_jsxs(StyledTable, {
      children: [/*#__PURE__*/_jsx(StyledCaption, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.datePicker.caption",
          options: {
            currentMonth
          }
        })
      }), /*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsx("tr", {
          className: weekDaysClasses,
          children: weekdayNames.map(dayName => {
            return /*#__PURE__*/_jsx("th", {
              scope: "col",
              children: /*#__PURE__*/_jsx("span", {
                className: "is--heading-6",
                children: dayName
              })
            }, dayName);
          })
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: weeks
      })]
    });
  }
  const localizedDate = I18n.moment(selectedDate).format('MMMM Do');
  return /*#__PURE__*/_jsxs(UIFlex, {
    direction: "column",
    align: "center",
    className: "date-picker",
    children: [/*#__PURE__*/_jsx(DatePickerHeader, {
      startOfMonth: startOfMonth,
      availableDateOptions: availableDateOptions,
      compact: compact,
      isTroubleshooting: isTroubleshooting,
      setMonthsAhead: setMonthsAhead,
      branding: branding,
      isLoadingLinkAvailabilityPage: isLoadingPage
    }), /*#__PURE__*/_jsx(StyledSkipNav, {
      onClick: () => {
        if (!compact && document.getElementById('selected-date')) {
          // There is no initial selected time  in compact view
          document.getElementById('selected-date').focus();
        }
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.datePicker.skipToAvailableDate",
        options: {
          monthDay: localizedDate
        }
      })
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        position: 'relative'
      },
      children: [renderWeeksOrEmpty(), isLoadingPage && /*#__PURE__*/_jsx(StyledOverlay, {
        contextual: true,
        children: /*#__PURE__*/_jsx(UILoadingDots, {})
      })]
    }), /*#__PURE__*/_jsx(DatePickerMarker, {}), /*#__PURE__*/_jsx(HiddenAriaStatus, {
      className: "calendar-status",
      role: "status",
      "aria-live": "polite",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "accessible.calendarStatus",
        options: {
          selectedDate: localizedDate
        }
      })
    })]
  });
}
DatePicker.propTypes = propTypes;
export default compose(EnsureAvailableDate, memo)(DatePicker);